import { useEffect, useState } from "react";

/**
 * Where the layout stops being a phone.
 *
 * §9.2 puts chat in the dock above this width and on the `/messages` route
 * below it, so the number is shared rather than written out at each site: a
 * breakpoint that disagrees with the Tailwind `md:` it is meant to mirror gives
 * you a rail that is hidden by CSS while the code around it still believes it
 * is there.
 */
export const DESKTOP = "(min-width: 768px)";

/** A one-shot read, for event handlers that only need to know right now. */
export function isDesktop(): boolean {
  return typeof window !== "undefined" && window.matchMedia(DESKTOP).matches;
}

/**
 * Subscribed, so a resize is a re-render.
 *
 * Reading `matchMedia` once during render would be right until somebody
 * changes the window - and the dock is the thing most likely to be wrong about
 * it, being present or absent on the strength of the answer.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === "undefined" ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const sync = () => setMatches(list.matches);

    // Once on mount too: the query may have changed between the initial state
    // and this effect running.
    sync();
    list.addEventListener("change", sync);

    return () => list.removeEventListener("change", sync);
  }, [query]);

  return matches;
}

export function useDesktop(): boolean {
  return useMediaQuery(DESKTOP);
}
